/**
 * Shared types for the agent core (docs/agent.md §3).
 *
 * This module — and everything under src/agent/ — is deliberately free of
 * React, Zustand and Y.Doc. Every capability arrives through a port on
 * RunContext, so the identical loop runs in the browser, in vitest against a
 * scripted LLM, and in Node against a real model. See §10: a future server-side
 * runner should need no changes to the tool contracts.
 */
import type { Edit } from '../../format/types';

// ---------------------------------------------------------------------------
// LLM transport (OpenAI chat-completions protocol)
// ---------------------------------------------------------------------------

export interface LlmToolCall {
  id: string;
  type: 'function';
  function: { name: string; arguments: string }; // arguments is a JSON string
}

/**
 * One message in the conversation sent upstream. Mirrors the OpenAI shape
 * because that is what every candidate provider speaks (§6).
 */
export interface LlmMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string | null;
  /** assistant messages only */
  tool_calls?: LlmToolCall[];
  /** tool messages only — which call this is the result of */
  tool_call_id?: string;
}

export interface LlmUsage {
  promptTokens: number;
  completionTokens: number;
}

export interface LlmRequest {
  messages: LlmMessage[];
  tools?: { type: 'function'; function: unknown }[];
  toolChoice?: 'auto' | 'none' | 'required';
  maxTokens?: number;
  temperature?: number;
}

/**
 * What a streaming completion yields. The transport's job is to turn provider
 * SSE into exactly these; everything downstream is provider-agnostic.
 */
export type LlmStreamEvent =
  | { type: 'text-delta'; text: string }
  | { type: 'tool-calls'; calls: LlmToolCall[] } // accumulated, at end of turn
  | { type: 'usage'; usage: LlmUsage }
  | { type: 'finish'; reason: string };

/**
 * The only LLM port. In the app it posts to /api/agent/chat (the Django proxy
 * holds the key and pins the model); in evals it talks to a provider directly;
 * in unit tests it is a scripted array of canned turns.
 */
export type LlmTransport = (
  req: LlmRequest,
  signal: AbortSignal,
) => AsyncIterable<LlmStreamEvent>;

// ---------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------

/**
 * `read` tools only look; `mutate` tools change the run-local draft and append
 * to the op log; `present` tools have visible side effects in chat but never
 * touch the timeline; `control` is propose (and any future run-control verb).
 *
 * Only `mutate` ops are replayable — the kind is what decides whether a call is
 * recorded in the op log at all.
 */
export type ToolKind = 'read' | 'mutate' | 'present' | 'control';

export interface ToolDef {
  name: string;
  kind: ToolKind;
  /** Sent to the model. Terse; the schema carries the detail. */
  description: string;
  /** JSON Schema for the arguments object. Kept small and flat on purpose (§7). */
  schema: JsonSchema;
  /**
   * Dotted paths in the args that hold ids this run created ('clip',
   * 'clips.*', 'track'). Replay rewrites these through its id map — without
   * this a replayed op would reference ids from the draft that never existed
   * on the live timeline.
   */
  idArgPaths?: string[];
  /** Human-readable one-liner for the proposal card's op list. */
  describeCall(args: Record<string, unknown>): string;
  /** Max characters of the JSON result handed back to the model (§7). */
  resultBudget?: number;
}

/**
 * The subset of JSON Schema the catalog uses. Hand-rolled rather than pulling
 * in ajv: the schemas are small and flat, and a dependency-free validator keeps
 * the core runnable anywhere.
 */
export interface JsonSchema {
  type: 'object';
  properties: Record<string, JsonSchemaProp>;
  required?: string[];
}

export interface JsonSchemaProp {
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object';
  description?: string;
  /**
   * Alternatives, checked in order; the value passes if any does. Used where a
   * plain string and a small object are both natural — a trajectory waypoint
   * is "sad" or {"q": "sad", "filters": {...}}. `type` is then the wire
   * schema's fallback for providers that ignore anyOf, and is what the branches
   * are checked against only when anyOf is absent.
   */
  anyOf?: JsonSchemaProp[];
  enum?: (string | number)[];
  items?: JsonSchemaProp;
  properties?: Record<string, JsonSchemaProp>;
  required?: string[];
  minimum?: number;
  maximum?: number;
  maxItems?: number;
}

/**
 * Executor results are returned to the model as tool messages — including
 * failures. An `error` is never thrown: self-correction from an instructive
 * error string is the core cheap-model strategy (§3), so every message should
 * say what to do instead, not just what went wrong.
 */
export type ToolResult =
  | { ok: true; [key: string]: unknown }
  | { ok: false; error: string };

export function toolError(message: string): ToolResult {
  return { ok: false, error: message };
}

// ---------------------------------------------------------------------------
// Op log
// ---------------------------------------------------------------------------

/**
 * One recorded mutating tool call. `produced` is the ids the executor created,
 * in the order it created them — replay zips these against the ids its own
 * re-execution produces to build the remap table (§5.2).
 */
export interface Op {
  tool: string;
  args: Record<string, unknown>;
  produced: string[];
}

// ---------------------------------------------------------------------------
// Run events, status, and the chat/proposal payloads the sinks receive
// ---------------------------------------------------------------------------

/**
 * Tool events carry enough to reconstruct the step afterwards, because the
 * assistant's steps are user-inspectable (docs/agent.md §4).
 *
 * `seq`, not `id`, is the identity of a step. `id` is the provider's tool-call
 * id, and llm.ts falls back to `call_${index}` with a fresh accumulator per
 * stream — so a provider that omits ids emits `call_0` on step 1 AND on step 5.
 * Matching on it marks the wrong row.
 */
export type AgentEvent =
  | { type: 'text-delta'; text: string }
  | {
      type: 'tool-start';
      id: string;
      seq: number;
      tool: string;
      kind: ToolKind | 'unknown';
      /** Parsed args; `{}` when the model sent malformed JSON — see `rawArgs`. */
      args: Record<string, unknown>;
      /** Exactly what the model emitted, so a malformed call is still legible. */
      rawArgs: string;
      label: string;
    }
  | {
      type: 'tool-result';
      id: string;
      seq: number;
      tool: string;
      result: ToolResult;
      /**
       * The tool message content as sent to the model — already clipped to the
       * tool's `resultBudget`, and therefore NOT necessarily valid JSON.
       */
      content: string;
      ms: number;
      /** Mutate tools: the resolved args recorded in the op log, not the model's. */
      opArgs?: Record<string, unknown>;
      /** `propose`: the op log it froze into a proposal. */
      ops?: Op[];
    }
  | { type: 'usage'; usage: LlmUsage }
  | { type: 'done'; reason: RunEndReason }
  | {
      type: 'error';
      message: string;
      /** Proxy error code when the failure came from the transport (§6). */
      code?: string;
    };

export type RunEndReason =
  | 'complete'
  | 'cancelled'
  | 'step-cap'
  | 'tool-cap'
  | 'timeout'
  | 'error';

export interface RunStatus {
  runId: string;
  state: 'running' | 'idle';
  label: string;
}

/** A candidate clip shown in chat. Times are CONCAT seconds (whole-item time). */
export interface ClipCard {
  archive: string;
  item: string;
  title: string;
  in: number;
  out: number;
  /** the agent's one-line reason for picking it */
  note?: string;
}

export type Attachment =
  /** The agent's own pick, from show_clips. */
  | { type: 'clips'; clips: ClipCard[] }
  /** A whole moment search, shown in full — see crdt/chat.ts for the contract. */
  | { type: 'moments'; label: string; total: number; more?: boolean; clips: ClipCard[] }
  | { type: 'proposal'; proposalId: string };

export interface AgentChatDraft {
  body: string;
  atts?: Attachment[];
}

export interface ProposalDraft {
  title: string;
  summary: string;
  ops: Op[];
}

// ---------------------------------------------------------------------------
// RunContext — every capability the loop has, injected
// ---------------------------------------------------------------------------

export interface BinEntryLike {
  id: string;
  title: string;
  archive?: string;
  item?: string;
  in?: number;
  out?: number;
}

/** Read-only glimpses of app state the prompt and tools resolve "this" against. */
export interface LiveState {
  selection: string[];
  playhead: number;
  bin: BinEntryLike[];
}

/**
 * Side-effect ports. The UI supplies real implementations (write to the chat
 * Y.Array and the `agent` root); evals and unit tests supply collectors.
 */
export interface RunSinks {
  postChat(msg: AgentChatDraft): void;
  publishProposal(p: ProposalDraft): string; // returns the new proposal id
  status(s: RunStatus): void;
}

/**
 * The run-local draft. Starts as a deep clone of the live timeline; mutate
 * tools read and replace it. Nothing here ever touches the shared document —
 * the draft only reaches the real timeline via propose → apply → replay.
 */
export interface DraftPort {
  get(): Edit;
  set(edit: Edit): void;
}

export interface RunLimits {
  maxSteps: number;
  maxToolCalls: number;
  wallClockMs: number;
  /** Conversation messages kept verbatim before older turns are dropped. */
  historyWindow: number;
}

/**
 * Raised from the plan's 16/40/120s. A multi-track edit with keyframes spends
 * calls fast: search, verify durations with get_item, add a track, place clips,
 * transition, then animate. 40 was tight enough that a run could hit the cap
 * mid-edit and auto-propose something half-finished, which is a worse failure
 * than costing a few cents more. Tune against real transcripts in the evals.
 */
export const DEFAULT_LIMITS: RunLimits = {
  maxSteps: 24,
  maxToolCalls: 64,
  wallClockMs: 180_000,
  historyWindow: 12,
};
